import { useTranslation } from "react-i18next";
import { ArrowDownUp, Layers, List, SlidersHorizontal } from "lucide-react";
import { Dialog } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { FilterField } from "../../roles/FilterField.js";
import { ManualListFiltersPanel } from "./ManualListFiltersPanel.js";
import { advancedFiltersActive, anyFilterActive, type ListSortKey, type ManualListFilters } from "./manual-list-model.js";
import type { ListPage, ListPageState } from "./useDashboardListStore.js";

/** Sort-label KEYS — module-level, so it cannot call a hook. */
const SORT_LABEL_KEY = {
  name: "dashboard.sortName",
  status: "dashboard.sortStatus",
  created: "dashboard.sortCreated",
  edited: "dashboard.sortEdited",
  favourited: "dashboard.sortFavourited",
} as const satisfies Record<ListSortKey, string>;

/**
 * The legacy `ManualList` chrome (ADR-034 D2): the name filter, the sort dropdown + direction, the group-by-facility
 * toggle, the "More filters" popover (a dot when any is set) and the result line "n of m manuals · Reset".
 */
export function ManualListToolbar({
  mode,
  state,
  shown,
  total,
  onPatch,
  onFilters,
  onReset,
  ratingsEnabled,
}: {
  mode: ListPage;
  state: ListPageState;
  shown: number;
  total: number;
  onPatch: (patch: Partial<ListPageState>) => void;
  onFilters: (patch: Partial<ManualListFilters>) => void;
  onReset: () => void;
  ratingsEnabled: boolean;
}) {
  const { t } = useTranslation();
  const sortKeys: ListSortKey[] = mode === "favourites" ? ["favourited", "name", "status", "created", "edited"] : ["name", "status", "created", "edited"];
  const advanced = advancedFiltersActive(state.filters);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <FilterField label={t("dashboard.filterManualsByName")} placeholder={t("dashboard.filterByName")} value={state.filters.name} onChange={(name) => onFilters({ name })} className="w-full sm:w-64" />
        <Select aria-label={t("dashboard.sortBy")} selectedKey={state.sort} onSelectionChange={(k) => onPatch({ sort: k as ListSortKey })} className="w-40">
          <SelectTrigger />
          <SelectContent>
            {sortKeys.map((k) => (
              <SelectItem key={k} id={k}>
                {t(SORT_LABEL_KEY[k])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon-sm" aria-label={t("dashboard.reverseSortOrder")} aria-pressed={state.reverse} onPress={() => onPatch({ reverse: !state.reverse })}>
          <ArrowDownUp aria-hidden="true" className="size-4" />
        </Button>
        <Button variant="outline" size="icon-sm" aria-label={t("dashboard.groupByFacility")} aria-pressed={state.grouped} onPress={() => onPatch({ grouped: !state.grouped })}>
          {state.grouped ? <Layers aria-hidden="true" className="size-4" /> : <List aria-hidden="true" className="size-4" />}
        </Button>
        <PopoverTrigger>
          <Button variant="outline" size="icon-sm" aria-label={advanced ? t("dashboard.moreFiltersActive") : t("dashboard.moreFilters")} className="relative">
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            {advanced ? <span aria-hidden="true" className="bg-primary absolute -top-0.5 -right-0.5 size-2 rounded-full" /> : null}
          </Button>
          <Popover className="w-80" placement="bottom end">
            <Dialog aria-label={t("dashboard.moreFilters")} className="outline-none">
              {({ close }) => (
                <ManualListFiltersPanel
                  mode={mode}
                  filters={state.filters}
                  onChange={onFilters}
                  onReset={onReset}
                  onClose={close}
                  archived={state.archived}
                  onArchivedChange={(archived) => onPatch({ archived })}
                  ratingsEnabled={ratingsEnabled}
                />
              )}
            </Dialog>
          </Popover>
        </PopoverTrigger>
      </div>
      <p className="text-muted-foreground flex items-center gap-2 text-xs" aria-live="polite">
        <span>
          {shown} of {total} manuals
        </span>
        {anyFilterActive(state.filters) ? (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onPress={() => onReset()}>
            {t("dashboard.resetFilters")}
          </Button>
        ) : null}
      </p>
    </div>
  );
}
